import { useState, useRef } from "react";
import useFocoModal from "../../hooks/useFocoModal";
import Icon from "../Icon";
import { BtnPrimary, BtnGhost } from "../Atoms";
import * as api from "../../api";

// Columnas esperadas del PDF/Excel en orden
const COLS = ["fecha","tipo","departamento","descripcion","destinatario","horas","prioridad","estado"];
const LABELS = { fecha:"Fecha", tipo:"Tipo", departamento:"Dpto.", descripcion:"Descripción", destinatario:"Destinatario", horas:"Horas", prioridad:"Prioridad", estado:"Estado" };

// Parsea horas en formato español "0,5" o "1.5"
const parseHours = s => parseFloat(String(s||"0").replace(",",".")) || 0;

// Parsea fecha "2026-04-07" o "07/04/2026" → "YYYY-MM-DD"
const parseDate = s => {
  if (!s) return null;
  s = s.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2,"0")}-${m[1].padStart(2,"0")}`;
  return s;
};

function parseTSV(raw) {
  const lines = raw.split("\n").map(l => l.trimEnd()).filter(Boolean);
  const rows = [];
  for (const line of lines) {
    const cells = line.split("\t");
    if (cells.length < 2) continue; // líneas de una sola celda las ignora
    // Si la primera celda parece una cabecera (Fecha, Date…) la saltamos
    if (/^fecha$/i.test(cells[0].trim())) continue;
    const entry = {};
    COLS.forEach((col, i) => { entry[col] = (cells[i] || "").trim(); });
    if (!entry.fecha) continue;
    rows.push(entry);
  }
  return rows;
}

function RowPreview({ rows }) {
  const totalH = rows.reduce((s, r) => s + parseHours(r.horas), 0);
  return (
    <div style={{overflowX:"auto",marginTop:12}}>
      <div style={{marginBottom:6,fontSize:11.5,color:"var(--text2)"}}>
        <strong>{rows.length}</strong> entradas · <strong>{totalH.toFixed(1)}h</strong> totales
      </div>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:11.5}}>
        <thead>
          <tr>
            {COLS.map(c=>(
              <th key={c} style={{padding:"5px 8px",textAlign:"left",fontWeight:700,fontSize:10,textTransform:"uppercase",color:"var(--text2)",borderBottom:"2px solid var(--border)",whiteSpace:"nowrap"}}>
                {LABELS[c]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r,i)=>(
            <tr key={i} style={{background: i%2===0 ? "var(--surface2)" : "transparent"}}>
              {COLS.map(c=>(
                <td key={c} style={{padding:"4px 8px",borderBottom:"1px solid var(--border)",color: c==="fecha"?"var(--text2)":"var(--text)",maxWidth:220,overflow:"hidden",textOverflow:"ellipsis",whiteSpace: c==="descripcion"?"normal":"nowrap"}}>
                  {c==="fecha" ? parseDate(r[c]) : r[c] || <span style={{color:"var(--text2)",opacity:.4}}>—</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ImportCuadernoModal({ onClose, onImported, defaultDepartment = "" }) {
  const cajaRef = useRef(null);
  useFocoModal(cajaRef, true, onClose);
  const [raw,     setRaw    ] = useState("");
  const [rows,    setRows   ] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result,  setResult ] = useState(null);
  const [error,   setError  ] = useState(null);

  const handleParse = () => {
    setError(null);
    const parsed = parseTSV(raw);
    if (!parsed.length) { setError("No se encontraron filas válidas. Asegúrate de pegar la tabla con tabuladores."); return; }
    setRows(parsed);
  };

  const handleImport = async () => {
    if (!rows?.length) return;
    setLoading(true);
    setError(null);
    try {
      const entries = rows.map(r => ({
        entry_date:   parseDate(r.fecha),
        type:         r.tipo         || "Tarea Principal",
        // Sin dpto. en la fila se hereda el equipo de quien importa.
        department:   r.departamento || defaultDepartment,
        description:  r.descripcion  || "",
        recipient:    r.destinatario || "",
        hours:        parseHours(r.horas),
        priority:     r.prioridad    || "Media",
        status:       r.estado       || "Completada",
      }));
      const data = await api.importAgendaEntries(entries);
      setResult(data);
      onImported?.(data.entries);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.45)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div ref={cajaRef} role="dialog" aria-modal="true" style={{background:"var(--surface)",borderRadius:14,boxShadow:"var(--shadow-lg)",width:"100%",maxWidth:860,maxHeight:"90vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>

        {/* Header */}
        <div style={{padding:"16px 20px",borderBottom:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <Icon name="upload" size={16} color="var(--accent)"/>
            <span style={{fontWeight:700,fontSize:15}}>Importar entradas al Cuaderno</span>
          </div>
          <button aria-label="Cerrar" onClick={onClose} style={{minWidth:24,minHeight:24,alignItems:"center",justifyContent:"center",display:"inline-flex",background:"none",border:"none",cursor:"pointer",padding:4,color:"var(--text2)",display:"flex"}}>
            <Icon name="x-lg" size={16} color="var(--text2)"/>
          </button>
        </div>

        <div style={{flex:1,overflowY:"auto",padding:20}}>

          {result ? (
            /* Resultado final */
            <div style={{textAlign:"center",padding:"32px 20px"}}>
              <div style={{fontSize:48,marginBottom:12}}>✅</div>
              <div style={{fontWeight:800,fontSize:18,marginBottom:6}}>{result.imported} entradas importadas</div>
              <div style={{color:"var(--text2)",fontSize:13,marginBottom:24}}>Los registros ya aparecen en tu cuaderno.</div>
              <BtnPrimary onClick={onClose}>Cerrar</BtnPrimary>
            </div>
          ) : (
            <>
              {/* Instrucciones */}
              <div style={{background:"var(--surface2)",borderRadius:10,padding:"12px 14px",marginBottom:16,fontSize:12.5,color:"var(--text2)",lineHeight:1.6}}>
                <strong style={{color:"var(--text)"}}>¿Cómo importar?</strong><br/>
                Copia la tabla desde el PDF o Excel (selecciona todas las filas y pulsa Ctrl+C) y pégala aquí.<br/>
                Las columnas deben seguir el orden: <em>Fecha · Tipo · Departamento · Descripción · Destinatario · Horas · Prioridad · Estado</em>
              </div>

              {/* Textarea */}
              {!rows && (
                <>
                  <textarea aria-label="Contenido a importar"
                    value={raw}
                    onChange={e => { setRaw(e.target.value); setRows(null); setError(null); }}
                    placeholder={"Pega aquí la tabla copiada del PDF o Excel…\n\n2026-04-07\tTarea Principal\tTIC\tDescripción de la tarea\tDestinatario\t0,5\tMedia\tCompletada"}
                    style={{width:"100%",minHeight:160,padding:"10px 12px",borderRadius:9,border:"1.5px solid var(--border)",background:"var(--surface2)",color:"var(--text)",fontFamily:"monospace",fontSize:12,resize:"vertical",boxSizing:"border-box"}}
                  />
                  {error && <div style={{color:"#ef4444",fontSize:12,marginTop:6}}>{error}</div>}
                  <div style={{marginTop:12,display:"flex",justifyContent:"flex-end"}}>
                    <BtnPrimary onClick={handleParse} disabled={!raw.trim()}>
                      <Icon name="eye" size={12} color="#fff"/>Vista previa
                    </BtnPrimary>
                  </div>
                </>
              )}

              {/* Preview */}
              {rows && (
                <>
                  <RowPreview rows={rows}/>
                  {error && <div style={{color:"#ef4444",fontSize:12,marginTop:8}}>{error}</div>}
                  <div style={{marginTop:14,display:"flex",gap:8,justifyContent:"flex-end"}}>
                    <BtnGhost onClick={() => { setRows(null); setError(null); }}>
                      <Icon name="arrow-left" size={12} color="var(--text2)"/>Editar
                    </BtnGhost>
                    <BtnPrimary onClick={handleImport} disabled={loading}>
                      {loading
                        ? <><Icon name="arrow-repeat" size={12} color="#fff"/>Importando…</>
                        : <><Icon name="upload" size={12} color="#fff"/>Importar {rows.length} entradas</>
                      }
                    </BtnPrimary>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
